import React, { useEffect, useRef, useState } from 'react';

import { Button, SingleLineText, Modal, LabelAndInput } from '@bloomays-lib/ui.shared';
import Box from '@mui/material/Box';
import { IMission } from '@bloomays-lib/types.shared';
import { Upload } from './Upload';
import { ActivitySummary, ActionCRAReponseSignStatus, UploadFileActivity } from '../../types/components';
import CRAValidation, { BloomerSignatureData } from '../organisms/CRAValidation';
import { splitEmbedSignature } from '@bloomays-lib/utils.shared';
import { useAuthStore } from '../../stores';
import { useTranslation } from 'react-i18next';

export type ActionCraProps = {
  CRASignSent: boolean;
  cancelFunc: () => void;
  CRACanSendInvite: boolean;
  sendFunc: (signerFullname: string) => void;
  CRAUploaded: boolean;
  handleCloseAction: () => void;
  uploadCRA: UploadFileActivity;
  activity: ActivitySummary;
  refetchActivities: () => void;
  mission: IMission;
  isZeroCRA?: boolean;
};

const ActionCRA = ({
  CRASignSent,
  cancelFunc,
  CRACanSendInvite,
  sendFunc,
  handleCloseAction,
  CRAUploaded,
  uploadCRA,
  activity,
  refetchActivities,
  mission,
  isZeroCRA = false,
}: ActionCraProps) => {
  const { t } = useTranslation('bloomer');
  const [files, setFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
  const CRAFRef = useRef<HTMLDivElement>(null);
  const [loadingButtons, setLoadingButtons] = useState({ view: false, sign: false, cancel: false });
  const [openEmbed, setOpenEmbed] = useState(false);
  const [responseSign, setResponseSign] = useState<{ status: ActionCRAReponseSignStatus }>({
    status: ActionCRAReponseSignStatus.NOT_SEND,
  });
  const [responseUploadCRA, setResponseUploadCRA] = useState<{ status: ActionCRAReponseSignStatus }>({
    status: ActionCRAReponseSignStatus.NOT_SEND,
  });
  const [zeroCraConfirmed, setZeroCraConfirmed] = useState<boolean>(false);
  const auth = useAuthStore((state) => state.auth);
  const handleCloseEmbed = async () => {
    setOpenEmbed(false);
    setResponseSign({ ...responseSign, status: ActionCRAReponseSignStatus.LOADING });
    await refetchActivities();
    setResponseSign({ ...responseSign, status: ActionCRAReponseSignStatus.SEND });
  };

  const handleOpenEmbed = async () => {
    setOpenEmbed(true);
  };

  useEffect(() => {
    if (responseSign.status !== ActionCRAReponseSignStatus.LOADING) {
      setLoadingButtons({ view: false, sign: false, cancel: false });
    }
  }, [responseSign]);

  const SignatureBloomays = () => {
    const buttonSignDisabled =
      !CRACanSendInvite ||
      responseSign.status === ActionCRAReponseSignStatus.LOADING ||
      (!isZeroCRA && !CRAUploaded) ||
      (isZeroCRA && !zeroCraConfirmed);
    const buttonCancelDisabled = !CRACanSendInvite || responseSign.status === ActionCRAReponseSignStatus.LOADING;
    const { signDate } = activity.signingEmbedBloomerId
      ? splitEmbedSignature(activity.signingEmbedBloomerId)
      : { signDate: undefined };
    return (
      <div>
        <div>
          {CRASignSent ? (
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column nowrap',
                justifyContent: 'space-evenly',
                height: '250px',
              }}
            >
              {(activity.signedCount || 0) === 1 ? (
                <>
                  <SingleLineText
                    text={`Tu as déjà signé ton CRA ${signDate ? `le ${signDate.toLocaleDateString('fr-FR')}` : ''}`}
                  />
                  <Button
                    textButton={'Annuler ma demande de signature ❌'}
                    disable={buttonCancelDisabled}
                    onClick={async () => {
                      setTimeout(() => {
                        setResponseSign({ status: ActionCRAReponseSignStatus.LOADING });
                        setLoadingButtons((prev) => ({ ...prev, cancel: true }));
                      }, 1);
                      await cancelFunc();
                      setResponseSign({ status: ActionCRAReponseSignStatus.NOT_SEND });
                      setLoadingButtons((prev) => ({ ...prev, cancel: false }));
                    }}
                    loading={loadingButtons.cancel}
                  />
                </>
              ) : (
                <SingleLineText text="CRA déjà signé par le client également." />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column nowrap',
                justifyContent: 'space-evenly',
                height: '250px',
              }}
            >
              <SingleLineText
                text={
                  loadingButtons.sign
                    ? 'Création de la signature électronique en cours...'
                    : "Mon compte rendu d'activité n'est pas encore signé !"
                }
              />
              {isZeroCRA && (
                <Box sx={{ mt: 2, textAlign: 'left' }}>
                  <LabelAndInput
                    name="confirmZeroCRA"
                    textLabel={t('confirmZeroCRA')}
                    type="checkbox"
                    checked={zeroCraConfirmed}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setZeroCraConfirmed(event.target.checked);
                    }}
                    id="confirmZeroCRA"
                    readOnly={false}
                  />
                </Box>
              )}
              <Button
                textButton={'Je signe mon CRA ✒️'}
                disable={buttonSignDisabled}
                onClick={async () => {
                  setLoadingButtons((prev) => ({ ...prev, sign: true }));
                  if (isZeroCRA) {
                    const resolvedSignerFullname = auth?.user?.displayName || activity.bloomerFullname?.[0] || '';
                    await sendFunc(resolvedSignerFullname);
                    setResponseSign({ status: ActionCRAReponseSignStatus.SEND });
                    handleCloseAction();
                    setLoadingButtons((prev) => ({ ...prev, sign: false }));
                    return;
                  }
                  await handleOpenEmbed();
                }}
                loading={loadingButtons.sign}
              />
            </Box>
          )}
        </div>
      </div>
    );
  };

  const title = mission.enableCRAUpload ? 'Upload' : 'Signature';

  const disabledCRAUploadBtn = () => {
    return (
      !files ||
      responseUploadCRA.status === 'loading' ||
      responseUploadCRA.status === 'send' ||
      files?.length > 2 ||
      files?.length === 0 ||
      rejectedFiles?.length > 0
    );
  };

  const onBloomerSign = async (signatureData: BloomerSignatureData) => {
    setTimeout(() => {
      setResponseSign({ status: ActionCRAReponseSignStatus.LOADING });
    }, 10);
    await sendFunc(signatureData.signerName);
    setResponseSign({ status: ActionCRAReponseSignStatus.SEND });
    setOpenEmbed(false);
    handleCloseAction();
  };

  return (
    <div>
      <Modal
        open={openEmbed}
        additionnalCSS={{
          width: '80%',
          minWidth: '740px',
          height: '99%',
          border: 'none',
          borderRadius: '20px',
          overflow: 'scroll',
        }}
        onClose={handleCloseEmbed}
      >
        <CRAValidation activitySummary={activity} readOnly={false} onBloomerSign={onBloomerSign}></CRAValidation>
      </Modal>

      <div>
        <SingleLineText sx={{ textAlign: 'left' }} variant={'heading6'} text={`${title} du compte rendu d'activité`} />
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'center',
            margin: '1em 0',
          }}
        >
          {!mission.enableCRAUpload ? (
            <SignatureBloomays />
          ) : (
            <div
              style={{
                width: '100%',
              }}
            >
              <div
                style={{
                  margin: '0 auto 0 0',
                  textAlign: 'left',
                }}
              >
                <SingleLineText variant={'caption'} text={'Uploader mon CRA signé en entreprise'} />
                <Upload
                  ref={CRAFRef}
                  id="CRAS3"
                  placeholder="Compte rendu d'activité"
                  textLabel={`Choisir mon CRA`}
                  name={'CRAS3'}
                  fileTypes={['.pdf']}
                  maxFiles={1}
                  maxLength={5000000}
                  displayIcons={true}
                  handleChange={(files: any, reject: any) => {
                    setRejectedFiles(reject);
                    setFiles(files);
                  }}
                  autoload={true}
                  preloadedFiles={files || []}
                />
              </div>
              <Button
                textButton="Uploader !"
                onClick={async () => {
                  setTimeout(() => {
                    setResponseUploadCRA({ status: ActionCRAReponseSignStatus.LOADING });
                  }, 10);
                  await uploadCRA(activity.recordId, { urlCraS3: files[0] });
                  setResponseUploadCRA({ status: ActionCRAReponseSignStatus.SEND });
                  setResponseUploadCRA({ status: ActionCRAReponseSignStatus.LOADING });
                  handleCloseAction();
                }}
                disable={disabledCRAUploadBtn()}
              />
            </div>
          )}
        </Box>
      </div>
    </div>
  );
};

export default ActionCRA;

export { ActionCRA };
